import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationThreadResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { EmailThreadView } from "../components/email-thread-view";
import { UnsupportedThreadView } from "../components/unsupported-thread-view";
import { useConversationView } from "../hooks/use-conversation-view";

interface ConversationViewProps {
  conversationId: ConversationId;
}

/**
 * Conversation view component that displays conversation data.
 * Renders threads using channel-appropriate components.
 */
export const ConversationView = memo(function ConversationView({
  conversationId,
}: ConversationViewProps) {
  const navigate = useNavigate();
  const { conversation, threads, isLoading, customerName } =
    useConversationView(conversationId);

  if (isLoading) {
    return (
      <Flex align="center" justify="center" p="xl">
        <RedoSpinner />
      </Flex>
    );
  }

  if (!conversation) {
    return (
      <Flex align="center" justify="center" p="xl">
        <Text>Conversation not found</Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" height="full" overflow="hidden" w="full">
      {/* Simple header - will be replaced by ConversationHeader in future PR */}
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        gap="md"
        p="md"
      >
        <RedoButton
          hierarchy="tertiary"
          IconLeading={ArrowLeftIcon}
          onClick={() => navigate(-1)}
          size="sm"
        />
        <Text fontSize="md" fontWeight="semibold">
          {customerName}
        </Text>
      </Flex>

      <Flex grow={1} overflow="hidden">
        {/* Center content - conversation threads */}
        <Flex
          bgColor="secondary"
          dir="column"
          grow={1}
          overflowX="hidden"
          overflowY="auto"
          p="xl"
        >
          <Flex dir="column" gap="xl" maxW="xl" mx="auto" w="full">
            {threads.length === 0 ? (
              <Flex align="center" grow={1} justify="center">
                <Text fontSize="md" textColor="secondary">
                  No messages in this thread
                </Text>
              </Flex>
            ) : (
              threads.map((thread) => (
                <ThreadRenderer key={thread.id} thread={thread} />
              ))
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});

interface ThreadRendererProps {
  thread: ConversationThreadResponse;
}

/**
 * Renders a thread using the appropriate component based on integration type.
 * This switch is exhaustive - TypeScript will error if a new integration type
 * is added without handling it here.
 */
const ThreadRenderer = memo(function ThreadRenderer({
  thread,
}: ThreadRendererProps) {
  // Route to appropriate thread component based on integration type
  // No default case - ensures compile-time error when new types are added
  switch (thread.integrationType) {
    case "gmail":
    case "outlook":
      return <EmailThreadView thread={thread} />;
    case "facebook":
      return <UnsupportedThreadView channel={thread.integrationType} />;
    case "instagram":
      return <UnsupportedThreadView channel={thread.integrationType} />;
    case "tiktok":
      return <UnsupportedThreadView channel={thread.integrationType} />;
    case "attentive":
      return <UnsupportedThreadView channel={thread.integrationType} />;
    case "postscript":
      return <UnsupportedThreadView channel={thread.integrationType} />;
  }
});
